import React, {
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Tooltip } from "primereact/tooltip";
import Icon from "./Icon";
import { useSidebarFavoritesStore } from "~/store/sidebarFavoritesStore";
import ObjectsDropdown from "./ObjectsDropdown";
// Lazy load heavy modal
const ActiveObjectsModal = lazy(() => import("../Modals/ActiveObjectsModal"));
import { Button } from "primereact/button";
import { useThemeStore, getThemeColors } from "~/store/themeStore";
import "./ObjectsSidebar.css";
import ColorInput from "./ColorInput";
import { getScopedItem, setScopedItem } from "~/store/workspaceStore";
import useWorkspaceStore from "~/store/workspaceStore";
import { getSidebarViewModeKey } from "~/workspace/constants";
import {
  TOOL_ICON_SIZE,
  TOGGLE_ICON_SIZE,
  BTN_HEIGHT,
  GRID_SIDEBAR_WIDTH,
  LIST_SIDEBAR_WIDTH,
} from "./sidebarConstants";
import { useSettingsStore } from "~/store/settingsStore";

const NO_CHART_DISABLED_STYLE = { opacity: 0.35, pointerEvents: "none" };

const ObjectsSidebar = ({
  handleAddFavorite,
  favorites,
  interactiveApi = null,
}) => {
  const sidebarOpen = useSidebarFavoritesStore((state) => state.sidebarOpen);
  const getSortedFavorites = useSidebarFavoritesStore(
    (state) => state.getSortedFavorites,
  );
  // interactiveApi must be provided by parent (chart). If not present, render without interactive controls.
  const api = interactiveApi ?? null;
  // Active Objects reads from the focused chart — with no chart focused there is
  // nothing to read, so disable it (drawing tools stay enabled: they self-select
  // the chart on mouse interaction).
  const focusedPanelType = useWorkspaceStore((state) => state.focusedPanelType);
  const noChartSelected = focusedPanelType !== "chart";
  const theme = useThemeStore((s) => s.theme);
  const themeColors = getThemeColors(theme);

  const favoritesListRef = useRef(null);
  const favoritesContainerRef = useRef(null);

  // View mode: 'grid' (icons only, 36px) or 'list' (icon + name, ~150px) - user-scoped
  const [viewMode, setViewMode] = useState(
    () => getScopedItem(getSidebarViewModeKey) || "grid",
  );

  // Save view mode to scoped localStorage
  useEffect(() => {
    setScopedItem(getSidebarViewModeKey, viewMode);
  }, [viewMode]);

  const sidebarWidth =
    viewMode === "grid" ? GRID_SIDEBAR_WIDTH : LIST_SIDEBAR_WIDTH;

  // (selection detection handled locally inside CursorToggle and per-favorite)

  // Check if favorites list is scrollable and add visual indicator
  const checkScrollable = useCallback(() => {
    const container = favoritesContainerRef.current;
    const list = favoritesListRef.current;

    if (container && list) {
      const isScrollable = list.scrollHeight > list.clientHeight;
      if (isScrollable) {
        container.classList.add("has-scroll");
      } else {
        container.classList.remove("has-scroll");
        container.classList.remove("scrolled-to-bottom");
      }
    }
  }, []);

  const checkScrollPosition = useCallback(() => {
    const container = favoritesContainerRef.current;
    const list = favoritesListRef.current;

    if (container && list) {
      const isAtBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight < 10;
      const isAtTop = list.scrollTop < 10;

      if (isAtBottom) {
        container.classList.add("scrolled-to-bottom");
      } else {
        container.classList.remove("scrolled-to-bottom");
      }

      if (!isAtTop) {
        container.classList.add("scrolled-down");
      } else {
        container.classList.remove("scrolled-down");
      }
    }
  }, []);

  // Handle Escape key to return to cursor mode when an interactive object is active
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        api &&
        api.activeInteractive &&
        typeof api.setActiveInteractive === "function"
      ) {
        console.log(
          "Escape pressed: returning to cursor mode from",
          api.activeInteractive,
        );
        api.setActiveInteractive(null);
      }
    };

    // Add event listener when component mounts
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [api]);

  // Monitor favorites list for scroll indicators
  useEffect(() => {
    checkScrollable();

    const list = favoritesListRef.current;
    if (list) {
      list.addEventListener("scroll", checkScrollPosition);
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(list);

      return () => {
        list.removeEventListener("scroll", checkScrollPosition);
        resizeObserver.disconnect();
      };
    }
  }, [favorites, checkScrollable, checkScrollPosition]);

  if (!sidebarOpen) return null;

  return (
    <div
      className="sidebar"
      style={{
        zIndex: 1000,
        width: `${sidebarWidth}px`,
        minWidth: `${sidebarWidth}px`,
        maxWidth: `${sidebarWidth}px`,
      }}
    >
      <div
        className="sidebar-header"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: viewMode === "grid" ? "center" : "stretch",
          gap: "0px",
          width: "100%",
          padding: viewMode === "list" ? "0 0px" : "0",
        }}
      >
        {/* View mode toggle - First row inside a frame */}
        <div
          className="sidebar-view-toggle-frame"
          style={{
            border: "1px solid var(--surface-border)",
            borderRightWidth: "2px",
            borderRadius: "4px",
            padding: "2px",
            marginTop: "3px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            className="sidebar-view-toggle"
            style={{
              display: viewMode === "grid" ? "grid" : "flex",
              gridTemplateColumns:
                viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
              flexDirection: viewMode === "list" ? "column" : undefined,
              gap: "1px",
              width: "100%",
            }}
          >
            <Button
              className="p-button-text p-button-sm"
              onClick={() => {
                setViewMode("grid");
              }}
              aria-label="Visualização em grade"
              aria-pressed={viewMode === "grid"}
              id="sidebar-view-grid"
              data-pr-tooltip={
                viewMode === "grid" ? "Ícones apenas" : undefined
              }
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: viewMode === "grid" ? "center" : "flex-start",
                background:
                  viewMode === "grid" ? "var(--surface-hover)" : "transparent",
                border: "none",
                borderRadius: "3px",
                padding: viewMode === "grid" ? "1px" : "1px 6px",
                width: "100%",
                height: `${BTN_HEIGHT}px`,
                gap: viewMode === "list" ? "6px" : "0",
              }}
              data-evo-id="objectssidebar-btn-view-grid"
            >
              <svg
                width={TOGGLE_ICON_SIZE}
                height={TOGGLE_ICON_SIZE}
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <rect x="1" y="1" width="6" height="6" />
                <rect x="9" y="1" width="6" height="6" />
                <rect x="1" y="9" width="6" height="6" />
                <rect x="9" y="9" width="6" height="6" />
              </svg>
              {viewMode === "list" && (
                <span
                  style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    color: "var(--text-color)",
                    marginLeft: "4px",
                    textAlign: "left",
                  }}
                >
                  Ícones Apenas
                </span>
              )}
              {viewMode === "grid" && <Tooltip target="#sidebar-view-grid" />}
            </Button>
            <Button
              className="p-button-text p-button-sm"
              onClick={() => {
                setViewMode("list");
              }}
              aria-label="Visualização em lista"
              aria-pressed={viewMode === "list"}
              id="sidebar-view-list"
              data-pr-tooltip={
                viewMode === "grid" ? "Ícones com nomes" : undefined
              }
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: viewMode === "grid" ? "center" : "flex-start",
                background:
                  viewMode === "list" ? "var(--surface-hover)" : "transparent",
                border: "none",
                borderRadius: "3px",
                padding: viewMode === "grid" ? "1px" : "1px 6px",
                width: "100%",
                height: `${BTN_HEIGHT}px`,
                gap: viewMode === "list" ? "6px" : "0",
              }}
              data-evo-id="objectssidebar-btn-view-list"
            >
              <svg
                width={TOGGLE_ICON_SIZE}
                height={TOGGLE_ICON_SIZE}
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <rect x="1" y="2" width="14" height="2" />
                <rect x="1" y="7" width="14" height="2" />
                <rect x="1" y="12" width="14" height="2" />
              </svg>
              {viewMode === "list" && (
                <span
                  style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    color: "var(--text-color)",
                    marginLeft: "4px",
                    textAlign: "left",
                  }}
                >
                  Ícones com Nomes
                </span>
              )}
              {viewMode === "grid" && <Tooltip target="#sidebar-view-list" />}
            </Button>
          </div>
        </div>

        {/* Second row - Objects dropdown and Active objects side by side in grid mode */}
        <div
          style={{
            display: viewMode === "grid" ? "grid" : "flex",
            gridTemplateColumns:
              viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
            flexDirection: viewMode === "list" ? "column" : undefined,
            gap: viewMode === "grid" ? "2px" : "0",
            width: "100%",
            alignItems: viewMode === "grid" ? "center" : "normal",
          }}
        >
          <ObjectsDropdown
            onAddFavorite={handleAddFavorite}
            favorites={favorites}
            interactiveApi={api}
            sidebarViewMode={viewMode}
            sidebarWidth={sidebarWidth}
          />

          {/* Active Objects Modal - Show list of active objects */}
          <div style={noChartSelected ? NO_CHART_DISABLED_STYLE : undefined}>
            <Suspense fallback={<div></div>}>
              <ActiveObjectsModal
                interactiveApi={api}
                viewMode={viewMode}
                sidebarWidth={sidebarWidth}
              />
            </Suspense>
          </div>
        </div>

        {/* Third row - Crosshair + Measure mode side by side in grid mode */}
        <div
          style={{
            display: viewMode === "grid" ? "grid" : "flex",
            gridTemplateColumns:
              viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
            flexDirection: viewMode === "list" ? "column" : undefined,
            gap: viewMode === "grid" ? "2px" : "0",
            width: "100%",
            marginTop: "2px",
          }}
        >
          <CrosshairToggle api={api} viewMode={viewMode} />
          <MeasureModeToggle viewMode={viewMode} />
        </div>

        {/* Fourth row - Color input + Cursor toggle side by side in grid mode */}
        <div
          style={{
            display: viewMode === "grid" ? "grid" : "flex",
            gridTemplateColumns:
              viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
            flexDirection: viewMode === "list" ? "column" : undefined,
            gap: viewMode === "grid" ? "2px" : "0",
            width: "100%",
            marginTop: viewMode === "grid" ? "2px" : "0",
          }}
        >
          <ColorInput viewMode={viewMode} />
          <CursorToggle api={api} viewMode={viewMode} />
        </div>

        {/* Fifth row - Continuous insertion toggle (full width) */}
        <div style={{ width: "100%", marginTop: "2px" }}>
          <ContinuousInsertionToggle viewMode={viewMode} />
        </div>
      </div>

      {/* Divider line */}
      <div
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: "var(--surface-border)",
          margin: "0",
        }}
      />

      <div
        ref={favoritesContainerRef}
        className="sidebar-favorites-container"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: `${sidebarWidth}px`,
          flex: 1,
          minHeight: 0,
        }}
      >
        <div className="sidebar-top-fade" />
        <div className="sidebar-top-chevron">⌃</div>
        <div
          ref={favoritesListRef}
          className={`sidebar-favorites sidebar-view-${viewMode}`}
          style={{
            display: viewMode === "grid" ? "grid" : "flex",
            gridTemplateColumns:
              viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
            flexDirection: viewMode === "list" ? "column" : undefined,
            alignItems: viewMode === "list" ? "stretch" : undefined,
            gap: viewMode === "grid" ? "2px" : "1px",
            maxHeight: "calc(100vh - 140px)",
            overflowY: "auto",
            padding:
              viewMode === "grid" ? "2px 2px 6px 2px" : "2px 0px 6px 0px",
            boxSizing: "border-box",
            width: "100%",
            maxWidth: `${sidebarWidth}px`,
          }}
        >
          {getSortedFavorites().map((fav, idx) => {
            // fav is { key, entry, categoryKey }
            const favKey =
              fav.key || (fav.entry && (fav.entry.key || fav.entry.propName));
            const object = fav.entry || {};
            // prefer explicit icon declared on the registry entry
            const iconId =
              object.icon ||
              object.key ||
              favKey ||
              object.propName ||
              "Trend_Line";
            const name =
              object.displayName || object.name || favKey || object.propName;
            const isActive =
              api &&
              (api.activeInteractive === favKey ||
                (api.objectsMap &&
                  Array.isArray(api.objectsMap[favKey]) &&
                  api.objectsMap[favKey].some((o) => o && o.selected)));
            const tooltipId = `favorite-tooltip-${idx}-${favKey}`;
            return (
              <div
                key={favKey || name}
                className={`favorite-item ${isActive ? "active" : ""} favorite-item-${viewMode}`}
                id={viewMode === "grid" ? tooltipId : undefined}
                data-pr-tooltip={viewMode === "grid" ? name : undefined}
                role="button"
                aria-pressed={!!isActive}
                onClick={() => {
                  if (!api || typeof api.setActiveInteractive !== "function")
                    return;
                  api.setActiveInteractive(isActive ? null : favKey);
                }}
                style={{
                  display: "flex",
                  justifyContent: viewMode === "grid" ? "center" : "flex-start",
                  alignItems: "center",
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: "100%",
                  height: `${BTN_HEIGHT}px`,
                  minHeight: `${BTN_HEIGHT}px`,
                  maxHeight: `${BTN_HEIGHT}px`,
                  boxSizing: "border-box",
                  border: `1px solid ${isActive ? themeColors.crosshair : "transparent"}`,
                  borderRadius: "4px",
                  padding: viewMode === "grid" ? "1px" : "0px 6px",
                  flexShrink: 0,
                  gap: viewMode === "list" ? "6px" : "0",
                }}
                data-evo-id={`objectssidebar-favorite-${favKey}`}
              >
                {viewMode === "grid" && <Tooltip target={`#${tooltipId}`} />}
                <Icon
                  iconId={iconId}
                  size={TOOL_ICON_SIZE}
                  className="favorite-icon"
                />
                {viewMode === "list" && (
                  <span
                    style={{
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                      textAlign: "left",
                    }}
                  >
                    {name}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ObjectsSidebar;

// Local component: toggle crosshair for the selected chart
function CrosshairToggle({ api, viewMode }) {
  const crosshairEnabled = useThemeStore((s) => s.crosshairEnabled);
  const toggleCrosshair = useThemeStore((s) => s.toggleCrosshair);
  const theme = useThemeStore((s) => s.theme);
  const themeColors = getThemeColors(theme);
  const iconColor = undefined;
  const isDrawingActive = api && api.activeInteractive;

  return (
    <Button
      className="p-button-text p-button-sm"
      onClick={() => !isDrawingActive && toggleCrosshair()}
      aria-label={crosshairEnabled ? "Disable crosshair" : "Enable crosshair"}
      aria-pressed={crosshairEnabled}
      disabled={!!isDrawingActive}
      id="crosshair-toggle-btn"
      data-pr-tooltip={
        viewMode === "grid"
          ? crosshairEnabled
            ? "Desabilitar cursor em cruz"
            : "Habilitar cursor em cruz"
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: viewMode === "grid" ? "center" : "flex-start",
        background: "transparent",
        boxSizing: "border-box",
        border: `1px solid ${crosshairEnabled ? themeColors.crosshair : "transparent"}`,
        borderRadius: "4px",
        padding: viewMode === "grid" ? "1px" : "1px 6px",
        width: "100%",
        height: `${BTN_HEIGHT}px`,
        minHeight: `${BTN_HEIGHT}px`,
        maxHeight: `${BTN_HEIGHT}px`,
        opacity: isDrawingActive ? 0.5 : 1,
        cursor: isDrawingActive ? "not-allowed" : "pointer",
        gap: viewMode === "list" ? "6px" : "0",
      }}
      data-evo-id="objectssidebar-btn-crosshair-toggle"
    >
      <Icon iconId="Crosshair" size={TOOL_ICON_SIZE} color={iconColor} />
      {viewMode === "list" && (
        <span
          style={{
            fontSize: "12px",
            whiteSpace: "nowrap",
            color: "var(--text-color)",
            textAlign: "left",
          }}
        >
          Cursor em Cruz
        </span>
      )}
      {viewMode === "grid" && <Tooltip target="#crosshair-toggle-btn" />}
    </Button>
  );
}

// Local component: switch to cursor/navigation mode by clearing the active interactive
function CursorToggle({ api, viewMode }) {
  const activeInteractive = api && api.activeInteractive;
  const isCursorMode = !activeInteractive;
  const anySelected =
    api &&
    api.objectsMap &&
    Object.values(api.objectsMap).some(
      (arr) => Array.isArray(arr) && arr.some((o) => o && o.selected),
    );
  const theme = useThemeStore((s) => s.theme);
  const themeColors = getThemeColors(theme);
  const crosshairEnabled = useThemeStore((s) => s.crosshairEnabled);

  const hasActiveDrawingOrSelection = activeInteractive || anySelected;
  const isDisabled = !hasActiveDrawingOrSelection;

  const handleClick = () => {
    if (!api || typeof api.setActiveInteractive !== "function" || isDisabled)
      return;
    api.setActiveInteractive(null);
  };

  const showBorder =
    !anySelected && isCursorMode && !crosshairEnabled && !isDisabled;

  return (
    <Button
      className="p-button-text p-button-sm"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label="Cancelar objeto selecionado"
      aria-pressed={showBorder}
      id="cursor-toggle-btn"
      data-pr-tooltip={
        viewMode === "grid" ? "Cancelar objeto selecionado" : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: viewMode === "grid" ? "center" : "flex-start",
        background: "transparent",
        boxSizing: "border-box",
        border: `1px solid ${showBorder ? themeColors.crosshair : "transparent"}`,
        borderRadius: "4px",
        padding: viewMode === "grid" ? "1px" : "1px 6px",
        width: viewMode === "grid" ? `${BTN_HEIGHT}px` : "100%",
        height: `${BTN_HEIGHT}px`,
        minWidth: viewMode === "grid" ? `${BTN_HEIGHT}px` : "auto",
        minHeight: `${BTN_HEIGHT}px`,
        maxWidth: viewMode === "grid" ? `${BTN_HEIGHT}px` : "100%",
        maxHeight: `${BTN_HEIGHT}px`,
        flexShrink: 0,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        gap: viewMode === "list" ? "6px" : "0",
      }}
      data-evo-id="objectssidebar-btn-cursor-toggle"
    >
      <Icon
        iconId="Direct_Select2"
        size={TOOL_ICON_SIZE}
        color={undefined}
        title="Cursor mode"
      />
      {viewMode === "list" && (
        <span
          style={{
            fontSize: "12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
            color: "var(--text-color)",
            textAlign: "left",
          }}
        >
          Cancelar Seleção
        </span>
      )}
      {viewMode === "grid" && <Tooltip target="#cursor-toggle-btn" />}
    </Button>
  );
}

CursorToggle.propTypes = {
  api: PropTypes.shape({
    activeInteractive: PropTypes.string,
    setActiveInteractive: PropTypes.func,
    objectsMap: PropTypes.object,
  }),
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

CrosshairToggle.propTypes = {
  api: PropTypes.shape({
    activeInteractive: PropTypes.string,
  }),
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

// Local component: toggle measure mode (Issue #101)
function MeasureModeToggle({ viewMode }) {
  const isMeasureModeActive = useWorkspaceStore(
    (state) => state.isMeasureModeActive,
  );
  const toggleMeasureMode = useWorkspaceStore(
    (state) => state.toggleMeasureMode,
  );
  const theme = useThemeStore((s) => s.theme);
  const themeColors = getThemeColors(theme);

  return (
    <Button
      className="p-button-text p-button-sm"
      onClick={toggleMeasureMode}
      aria-label={
        isMeasureModeActive ? "Desativar modo medição" : "Ativar modo medição"
      }
      aria-pressed={isMeasureModeActive}
      id="measure-mode-toggle-btn"
      data-pr-tooltip={
        viewMode === "grid"
          ? isMeasureModeActive
            ? "Desativar modo medição"
            : "Ativar modo medição (régua)"
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: viewMode === "grid" ? "center" : "flex-start",
        background: "transparent",
        boxSizing: "border-box",
        border: `1px solid ${isMeasureModeActive ? themeColors.crosshair : "transparent"}`,
        borderRadius: "4px",
        padding: viewMode === "grid" ? "1px" : "1px 6px",
        width: "100%",
        height: `${BTN_HEIGHT}px`,
        minHeight: `${BTN_HEIGHT}px`,
        maxHeight: `${BTN_HEIGHT}px`,
        gap: viewMode === "list" ? "6px" : "0",
      }}
      data-evo-id="objectssidebar-btn-measure-mode-toggle"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={TOOL_ICON_SIZE}
        height={TOOL_ICON_SIZE}
        viewBox="0 0 24 24"
        fill={isMeasureModeActive ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Diagonal ruler body */}
        <rect
          x="3"
          y="10"
          width="18"
          height="6"
          rx="1"
          transform="rotate(-45 12 12)"
        />
        {/* Tick marks */}
        <line x1="7" y1="11" x2="7" y2="13" transform="rotate(-45 12 12)" />
        <line x1="10" y1="10" x2="10" y2="13" transform="rotate(-45 12 12)" />
        <line x1="13" y1="11" x2="13" y2="13" transform="rotate(-45 12 12)" />
        <line x1="16" y1="10" x2="16" y2="13" transform="rotate(-45 12 12)" />
      </svg>
      {viewMode === "list" && (
        <span
          style={{
            fontSize: "12px",
            whiteSpace: "nowrap",
            color: "var(--text-color)",
            textAlign: "left",
          }}
        >
          Modo Medição
        </span>
      )}
      {viewMode === "grid" && <Tooltip target="#measure-mode-toggle-btn" />}
    </Button>
  );
}

MeasureModeToggle.propTypes = {
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

// Local component: toggle continuous object insertion mode (Issue #305)
function ContinuousInsertionToggle({ viewMode }) {
  const isActive = useSettingsStore(
    (state) => state.continuousObjectInsertion ?? false,
  );
  const setActive = useSettingsStore(
    (state) => state.setContinuousObjectInsertion,
  );
  const theme = useThemeStore((s) => s.theme);
  const themeColors = getThemeColors(theme);

  return (
    <Button
      className="p-button-text p-button-sm"
      onClick={() => setActive(!isActive)}
      aria-label={
        isActive ? "Desativar inserção contínua" : "Ativar inserção contínua"
      }
      aria-pressed={isActive}
      id="continuous-insertion-toggle-btn"
      data-pr-tooltip={
        viewMode === "grid"
          ? isActive
            ? "Desativar inserção contínua"
            : "Ativar inserção contínua de objetos"
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: viewMode === "grid" ? "center" : "flex-start",
        background: "transparent",
        boxSizing: "border-box",
        border: `1px solid ${isActive ? themeColors.crosshair : "transparent"}`,
        borderRadius: "4px",
        padding: viewMode === "grid" ? "1px" : "1px 6px",
        width: "100%",
        height: `${BTN_HEIGHT}px`,
        minHeight: `${BTN_HEIGHT}px`,
        maxHeight: `${BTN_HEIGHT}px`,
        gap: viewMode === "list" ? "6px" : "0",
      }}
      data-evo-id="objectssidebar-btn-continuous-insertion-toggle"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={TOOL_ICON_SIZE}
        height={TOOL_ICON_SIZE}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          d="M15 4.5l-4 4l-4 1.5l-1.5 1.5l7 7l1.5 -1.5l1.5 -4l4 -4 Z"
          fill={isActive ? "currentColor" : "none"}
        />
        <line x1="9" y1="15" x2="4.5" y2="19.5" />
        <line x1="14.5" y1="4" x2="20" y2="9.5" />
      </svg>
      {viewMode === "list" && (
        <span
          style={{
            fontSize: "12px",
            whiteSpace: "nowrap",
            color: "var(--text-color)",
            textAlign: "left",
          }}
        >
          Inserção Contínua
        </span>
      )}
      {viewMode === "grid" && (
        <Tooltip target="#continuous-insertion-toggle-btn" />
      )}
    </Button>
  );
}

ContinuousInsertionToggle.propTypes = {
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

ObjectsSidebar.propTypes = {
  handleAddFavorite: PropTypes.func,
  favorites: PropTypes.array,
  interactiveApi: PropTypes.shape({
    registeredTypes: PropTypes.array,
    activeInteractive: PropTypes.string,
    setActiveInteractive: PropTypes.func,
    objectsMap: PropTypes.object,
  }),
};
